/**
 * First-run tutorial: contextual popups from Start Run through the first
 * completed mission. Whether it runs is a per-run flag (menu checkbox,
 * defaulting ON until the player finishes or skips a tutorial once —
 * tracked in localStorage, independent of run saves).
 */
import { coachHoldsTip } from '~/utils/firstRunGuide'
import { readFlag, writeFlag } from '~/utils/flagStore'

export const TUTORIAL_DONE_KEY = 'elemental-dispatch-tutorial-done'

export interface TutorialStep {
  icon: string
  title: string
  /** 🗜️ v3.78 (per Javi: "not so wordy, just simple bullet points").
   * Every step is a short list now. The old paragraphs ran to fifty words
   * and arrived while the player was mid-decision, which is the worst
   * possible moment to hand someone prose. Keep each bullet under about
   * ten words, and no more than four bullets per step. */
  bullets: string[]
  /** The final step: dismissing it completes the tutorial. */
  last?: boolean
  /** No "Skip tutorial" link (for tips that can fire after the tutorial). */
  hideSkip?: boolean
  /** Stays armed after the tutorial ends (on runs that completed it) —
   * for triggers that usually happen later, like gold spawns/level-ups. */
  late?: boolean
  /** 🗺️ v7.05: a row of painted icons under the title (the draft card shows
   * the first stops' node art). Set per run by the page, not baked here. */
  art?: string[]
  /** 🎓 v7.20: the skip link's label key - the practice says "Skip practice",
   * because that is all it skips (the real run keeps its cards). */
  skipKey?: string
  /** 🗺️ v7.38 (per Javi): per-card glyph art over IconText's global map - the
   * map legend paints the MAP's own node art, whatever ⛺/🧩 wear elsewhere. */
  icons?: Record<string, string>
  /** 🎓 v7.39 (per Javi, F08): fires on a game EVENT mid-play (a gold spawn, a
   * level-up, a lost fight...), so it waits for ~1 s without a tap before it
   * appears - "A gold mission!" popped under a finger aiming at a marker. */
  event?: boolean
}

/** 🎓 v7.40: a practice card's id - PracticeCoach speaks for it (the
 * practice.* keys); the id only marks it seen with the run. */
const PRACTICE_CARD: TutorialStep = { icon: '', title: '', bullets: [] }

export const TUTORIAL_STEPS: Record<string, TutorialStep> = {
  // 🗑️ v3.64: the 'bosses' step pointed at the draft's boss lineup, which
  // is gone (it read as a row of things to pick). Boss stats and skills
  // are still previewable from the map node, where they mean something.
  // 🗜️ v2.22 compression (per Javi's scorecard): the draft screen used to
  // interrupt three separate times (scout → draft mechanics → relic pick)
  // before the player touched anything. One combined intro now covers all
  // three, dismissing it still opens the Route Preview and spotlights the
  // starting nodes. draft.vue swaps in a version naming the actual
  // first-node threats.
  // 🎓 v7.38 (per Javi): relic-free - normal (A0) campaign runs skip the starting
  // relic since v7.33/v7.37; draft.vue swaps in the tutorial.draftRelic.* lines on
  // the runs that still offer one (ascensions, the daily/weekly, the Gauntlet).
  draftIntro: {
    icon: '/ui/icon-scouting.webp',
    title: 'Scout, then draft your team',
    bullets: [
      'Draft creatures whose elements cover the threats ahead',
      'Tap creatures to pick them — Confirm appears once your team is full',
      'Got it opens the Route Preview',
    ],
  },
  map: {
    icon: '/ui/icon-map.webp',
    title: 'The forest road',
    bullets: [
      'Tap a glowing node to read it, then Take this path',
      '⚔️ fight · ⛺ heals · 🎁 relic · 🛒 shop · ❓ gamble · 🧩 trial',
      '✏️ Plan route sketches your path',
    ],
    // 🗺️ v7.38 (per Javi): the legend wears the map's own node art (data/nodes.ts) -
    // ⛺ and 🧩 keep their reward/puzzle art everywhere else; ⚔️ is a pair badge,
    // the mark every element-pair (combat) stop carries.
    icons: {
      '⚔️': '/ui/nodes/firewater.webp',
      '⚔': '/ui/nodes/firewater.webp',
      '⛺': '/ui/nodes/rest.webp',
      '🧩': '/ui/nodes/puzzle.webp',
    },
  },
  encounter: {
    icon: '/ui/icon-fight.webp',
    title: 'Your first fight',
    bullets: [
      'Clear the ⭐ goal to finish the node',
      'Missions expire on their own — that costs ❤️',
      '🗑️ discard costs the same as expiring — a fresh mission comes',
      '🏕️ camp pauses · tap an empty spot to fast-forward',
    ],
  },
  // 🧭 v5.12 (per Javi): a quick tour of the banner, right after the
  // first-fight intro — every pill in one glance.
  banner: {
    icon: '/ui/icon-victory.webp',
    title: 'The banner, at a glance',
    bullets: [
      'Act and node up top · 🗺️ peeks at the road ahead',
      'The clock runs only while you play',
      '⭐ node goal · 💰 gold for shops · ❤️ your life',
      'The corner menu pauses, mutes, or ends the run',
    ],
  },
  // 🗜️ v2.22: folds in what used to be a separate 'travel' popup shown
  // right after dispatch — one less interruption between "build the
  // team" and actually watching your creatures walk out.
  mission: {
    // 👆 v3.81: the two remaining emoji step icons take painted art —
    // the tap hand for the step that is entirely about tapping…
    icon: '/ui/icon-taphere.webp',
    title: 'Build the team',
    bullets: [
      'Tap creatures until every element is covered',
      'The % is your win chance',
      'Coverage you miss costs ❤️, win or lose',
      'Fight! — they walk out, fight, and walk back',
    ],
  },
  done: {
    icon: '/ui/icon-celebrate.webp',
    title: 'Mission complete!',
    bullets: [
      'That is the loop: missions, coverage, resolve',
      'Level up, earn 💰 for the 🛒 shop, reach the boss',
      'Good luck out there',
    ],
    last: true,
    event: true,
  },
  // Fires whenever the first gold mission shows up — even after `done`,
  // since gold usually spawns later than the first completed mission.
  gold: {
    icon: '/ui/icon-gold.webp',
    title: 'A gold mission!',
    bullets: [
      '💰 pays gold for the 🛒 shop',
      'It does not count toward the node goal',
      'Letting it expire is free — failing it is not',
    ],
    hideSkip: true,
    late: true,
    event: true,
  },
  bossfight: {
    icon: '/ui/icon-boss.webp',
    title: 'Boss fight!',
    bullets: [
      'Your coverage is your win chance',
      'Missing coverage hurts, win or lose',
      'Read its skill before you commit',
    ],
    hideSkip: true,
    late: true,
  },
  levelup: {
    // …and Field Promotion's medal for the step about gaining a level.
    icon: '/ui/rewards/fieldpromotion.webp',
    title: 'Level up!',
    bullets: [
      'A creature has a stat point waiting — it glows',
      'Spend it where the road ahead threatens',
      'Banking it for later is fine',
    ],
    hideSkip: true,
    late: true,
    event: true,
  },
  failure: {
    icon: '/ui/icon-damage.webp',
    title: 'That one hurt',
    bullets: [
      'Damage scales with the coverage you missed',
      'It lands win or lose',
      'Expired missions chip ❤️ too, at half',
      'At 0 ❤️ the run ends',
    ],
    hideSkip: true,
    late: true,
    event: true,
  },
  // Fires on the run's first puzzle node — the plan-then-begin flow is
  // unlike anything else in the game (v0.86). 🧩 v7.38: also once per device
  // on the first Puzzle Game level (see PUZZLE_TIP_KEY).
  puzzle: {
    icon: '/ui/icon-puzzle.webp',
    title: 'A trial of mirrors',
    bullets: [
      'Trials are dealt from your own team’s stats',
      'A clean split exists — each creature joins one',
      'Lock every trial, then ⚔️ Begin',
      'No timers — damage taken is your score',
    ],
    hideSkip: true,
    late: true,
  },
  // Fires the moment escalation kicks in mid-fight (v5.07, per Javi) —
  // a `late` tip, since enrage only arms after many spawns.
  enrage: {
    icon: '/ui/icon-enrage.webp',
    title: 'The horde turns furious',
    bullets: [
      'After nine missions, new arrivals enrage',
      'Every second they hit harder',
      'The 😡 pill shows the climb',
      'Clear the ⭐ goal fast — stalling only feeds it',
    ],
    hideSkip: true,
    late: true,
    event: true,
  },
  // Fires when the act's Training Grounds mission lands (v0.86).
  training: {
    icon: '/ui/rewards/trainingground.webp',
    title: 'The Training Grounds',
    bullets: [
      'Send exactly one creature',
      'Pick one of three skills — theirs forever',
      'No roll, no damage — just survive the node',
      'Short fuse — commit early',
    ],
    hideSkip: true,
    late: true,
    event: true,
  },
  // ✨ v6.85 (per Javi): the Starweave is an Ascension 5 unlock with its
  // own two-card tutorial - one on the title when it first opens, one
  // inside the modal on its first visit. Neither is run-bound: they use
  // the standalone flags below, not run.tutorialSeen.
  // 🎓 v7.04 (per Javi): the FIRST DISPATCH - a guided run for first-timers
  // (data/firstDispatch.ts). fd* steps fire only in mode 'tutorial'; the
  // classic tips fire only outside it (stepFor keeps the two apart).
  // 🎓 v7.40 (per Javi, D5): the practice speaks through PracticeCoach's
  // one-line storyboard (encounter.vue's practiceLine, the practice.* keys,
  // every number read off the lesson). These ids remain as its CARDS - each
  // holds the clock, and its button marks it seen with the run, so a reload
  // never shows it twice. The v7.04 coach cards (fdOpen, fdWatch, fdRetry)
  // retired with it: action prompts are lines now, not cards.
  // (No words of their own: nothing renders these as cards any more.)
  fdWelcome: { ...PRACTICE_CARD },
  fdExpire: { ...PRACTICE_CARD },
  // 🎓 v8.02 (per Javi): the Surge lesson's card, before the pair.
  fdSurge: { ...PRACTICE_CARD },
  fdCoverage: { ...PRACTICE_CARD },
  // 🎓 v8.02: the on-use skill lesson's card (Rillhopper's Skim, the glass).
  fdSkill: { ...PRACTICE_CARD },
  fdDone: { ...PRACTICE_CARD, last: true, event: true },
  starweaveIntro: {
    icon: '/ui/stardust.webp',
    title: 'The Starweave awakens',
    bullets: [
      'Ascension 5 opens a sky of talents',
      'Bind Stardust to stars — they last across runs',
      'Unbind between runs and it all comes back',
      'Find it under Profile → Starweave',
    ],
    hideSkip: true,
  },
  starweaveModal: {
    icon: '/ui/stardust.webp',
    title: 'Reading the sky',
    bullets: [
      'Tap a star to read it; linked stars open in order',
      'Every bound star raises all prices by 10%',
      'Novas need two full branches — choose with care',
      'Stars join your next new run, never the current one',
    ],
    hideSkip: true,
  },
}

/** ✨ v6.85: standalone tutorial flags (comma-separated ids), outside any run. */
export const STARWEAVE_TUTORIAL_KEY = 'elemental-dispatch-starweave-tutorial'

export function starweaveTutorialSeen(id: string): boolean {
  try { return (localStorage.getItem(STARWEAVE_TUTORIAL_KEY) ?? '').split(',').includes(id) } catch { return true }
}

export function markStarweaveTutorial(id: string): void {
  try {
    const seen = new Set((localStorage.getItem(STARWEAVE_TUTORIAL_KEY) ?? '').split(',').filter(Boolean))
    seen.add(id)
    localStorage.setItem(STARWEAVE_TUTORIAL_KEY, [...seen].join(','))
  } catch { /* private mode etc. */ }
}

/** 🧩 v7.38 (per Javi, critique #11): a Puzzle Game level opened straight from
 * the title never ran the tutorial, so it taught nothing. The puzzle card now
 * shows once per device on the first puzzle opened; dismissing it anywhere
 * (a campaign puzzle node too) marks it seen. */
export const PUZZLE_TIP_KEY = 'elemental-dispatch-puzzle-tip'
/** usePuzzleGame's PROGRESS_KEY: best damage per cleared level. */
const PUZZLE_PROGRESS_KEY = 'elemental-dispatch-puzzle-v1'

/** Seen = dismissed on this device, OR a Puzzle Game level already cleared
 * (a pre-v7.38 puzzler knows the trial - the card is for a FIRST open). */
export function puzzleTipSeen(): boolean {
  try {
    if (localStorage.getItem(PUZZLE_TIP_KEY) === '1') return true
    let best: unknown = null
    try { best = JSON.parse(localStorage.getItem(PUZZLE_PROGRESS_KEY) ?? 'null') } catch { /* a broken record clears nothing */ }
    return !!best && typeof best === 'object' && Object.keys(best).length > 0
  } catch { return true }
}

function markPuzzleTip(): void {
  try { localStorage.setItem(PUZZLE_TIP_KEY, '1') } catch { /* private mode etc. */ }
}

// 🎓 v7.69: through utils/flagStore.ts - localStorage, sessionStorage and memory, so a device whose
// localStorage refuses writes still remembers the answer for the session.
function markTutorialDone() {
  writeFlag(TUTORIAL_DONE_KEY, '1')
}

export function tutorialAlreadyDone(): boolean {
  return readFlag(TUTORIAL_DONE_KEY) === '1'
}

export function useTutorial() {
  const run = useRunState()
  const { t } = useLocale()

  // 🌍 v5.19: keys resolve here; a miss falls back to the English baked
  // into TUTORIAL_STEPS (t() returns the key itself when unknown).
  function localized(key: string, fallback: string): string {
    const got = t(key)
    return got === key ? fallback : got
  }

  /** The step to show right now on this page, if any — translated. */
  function stepFor(id: string): TutorialStep | null {
    const state = run.value
    if (!state) return null
    // 🎓 v7.04: the First Dispatch and the classic tips never share a run.
    if ((state.mode === 'tutorial') !== id.startsWith('fd')) return null
    // 🎓 v7.41 (per Javi, §3 C): a first run's first location speaks through
    // its guide - the one-liners stand in for the late cards until location 1
    // is cleared, and while the coach is on its arrival card and the sheet's
    // lines stand in for the first-fight ones (utils/firstRunGuide.ts).
    if (coachHoldsTip(state, id)) return null
    // `late` tips stay live after the tutorial ends (a completed `done`
    // marks this as a tutorial run) — their triggers rarely fire before.
    const eligible = state.tutorial
      || (TUTORIAL_STEPS[id]?.late === true && state.tutorialSeen.includes('done'))
      // 🧩 v7.38: a Puzzle Game session is never tutorial-eligible - its first level teaches once per device.
      || (id === 'puzzle' && typeof state.puzzleLevel === 'number' && !puzzleTipSeen())
    if (!eligible || state.tutorialSeen.includes(id)) return null
    const step = TUTORIAL_STEPS[id]
    if (!step) return null
    // 🎓 v7.40 (per Javi, D6b): a first run's ×3 is the banner's ⏩ button.
    // (🎓 v7.41: the coach holds this card back and says it as one-liners;
    // after "Stop guidance" the card comes back - with this line.)
    const firstRunLine = (i: number) => (id === 'encounter' && i === 3 && state.firstRun ? 'FirstRun' : '')
    return {
      ...step,
      title: localized(`tutorial.${id}.title`, step.title),
      bullets: step.bullets.map((b, i) => localized(`tutorial.${id}.b${i}${firstRunLine(i)}`, b)),
    }
  }

  function dismissStep(id: string) {
    if (id === 'puzzle') markPuzzleTip()
    const state = run.value
    if (!state || state.tutorialSeen.includes(id)) return
    state.tutorialSeen.push(id)
    if (TUTORIAL_STEPS[id]?.last) {
      state.tutorial = false
      markTutorialDone()
    }
  }

  function skipTutorial() {
    const state = run.value
    if (state) {
      state.tutorial = false
      // Mark everything seen so late tips (gold) don't fire post-skip.
      for (const id of Object.keys(TUTORIAL_STEPS)) {
        if (!state.tutorialSeen.includes(id)) state.tutorialSeen.push(id)
      }
    }
    // 🎓 v7.04 (per Javi): a skip only skips THIS run - the guide stays on
    // until a tutorial is finished once (dismissStep marks the last card).
  }

  /** ✨ v6.85: a step outside any run (the Starweave cards) - translated, ungated. */
  function standaloneStep(id: string): TutorialStep | null {
    const step = TUTORIAL_STEPS[id]
    if (!step) return null
    return {
      ...step,
      title: localized(`tutorial.${id}.title`, step.title),
      bullets: step.bullets.map((b, i) => localized(`tutorial.${id}.b${i}`, b)),
    }
  }

  return { stepFor, dismissStep, skipTutorial, standaloneStep }
}
